import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, MessageSquareWarning, NotebookPen } from 'lucide-react'
import { NOT_ENTITLED, get, write } from '../api'
import { dateTime, words } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
  Spinner,
} from '../ui'

// ======================================================================
// YOUR OWN INTERVIEW NOTES
// ======================================================================
//
// THE INTERVIEWEE'S ENTIRE SURFACE. Everything here has to work for
// somebody whose only capability is view_own_notes — a witness, a
// companion. They hold no view_case, cannot reach /meetings, /parties,
// /evidence, /notes or /allegations, and must not be shown a frame that
// implies those exist.
//
// THE NO-ARGUMENT LIST IS THE ENTRY POINT, because an interviewee is
// never told a meeting id. They get an email saying their notes are
// ready. Nothing appears here until an investigator sends a version: the
// query starts from er_note_confirmations, so a draft has no row to
// match.
//
// THERE IS NO FIELD NAMING ANYBODY ELSE. MyMeetingItem carries no
// interviewer, no other party, no attendee list — a witness must not
// learn from their own screen who else was interviewed. Nothing is added
// here.
//
// THE RESPONSE SITS BESIDE THE NOTE AND NEVER CHANGES IT. Responding
// writes responded_at, response and comment onto the confirmation row
// and touches nothing else. If the interviewee says "I never said that",
// the disagreement IS the record.
//
// SINGLE USE PER VERSION. A second response would in practice always be
// read as superseding the first, which is a way to withdraw a
// disagreement under pressure. The backend refuses it; this screen shows
// the refusal as worded and stops offering the control once a response
// exists.

export default function MyNotes({ caseId, meetings, standalone = false }) {
  const [open, setOpen] = useState(null)

  if (!meetings.length) {
    return (
      <Panel eyebrow="Your interview" title="Your notes">
        <Empty icon={NotebookPen} title="Nothing has been sent to you yet">
          When the notes of your interview are ready, they will appear here and
          you will be asked to confirm them or say what is wrong with them.
        </Empty>
      </Panel>
    )
  }

  return (
    <div className="space-y-4">
      {meetings.map((m) => (
        <Panel
          key={m.meeting_id}
          eyebrow="Your interview"
          title={words(m.kind)}
          right={
            m.awaiting_response ? (
              <Chip tone="amber">awaiting your response</Chip>
            ) : (
              <Chip tone={m.response === 'confirmed' ? 'accent' : 'blue'}>
                {m.response === 'confirmed' ? 'you confirmed these' : 'you commented'}
              </Chip>
            )
          }
        >
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-faint">
            {m.scheduled_at && <span>Interview on {dateTime(m.scheduled_at)}</span>}
            <span>Sent to you {dateTime(m.sent_at)}</span>
            {m.responded_at && <span>You replied {dateTime(m.responded_at)}</span>}
            {m.versions_sent > 1 && (
              <span>{m.versions_sent} versions have been sent to you</span>
            )}
          </div>

          <div className="mt-3">
            <Button
              variant={m.awaiting_response ? 'primary' : 'default'}
              icon={NotebookPen}
              onClick={() => setOpen(m.meeting_id)}
            >
              {m.awaiting_response ? 'Read and respond' : 'Read the notes'}
            </Button>
          </div>

          {open === m.meeting_id && (
            <div className="mt-4">
              <NoteVersions caseId={caseId} meetingId={m.meeting_id} />
            </div>
          )}
        </Panel>
      ))}

      {standalone && (
        <p className="px-1 text-[11.5px] leading-relaxed text-faint">
          This page shows the record of your own interview and nothing else. It
          does not show the case, the people involved in it, or anybody else’s
          account.
        </p>
      )}
    </div>
  )
}

function NoteVersions({ caseId, meetingId }) {
  const [versions, setVersions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [responding, setResponding] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // THIS GET WRITES. Opening the record of your own interview is
      // recorded — it is also the receipt that you were able to read it
      // before responding.
      const res = await get(`/${caseId}/my-notes/${meetingId}`)
      setVersions(res === NOT_ENTITLED ? null : res)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [caseId, meetingId])

  useEffect(() => {
    load()
  }, [load])

  if (loading) return <Spinner label="Opening the notes" />
  if (versions === null) return null

  return (
    <div className="space-y-3">
      <Refusal error={error} onDismiss={() => setError(null)} />

      {[...versions]
        .sort((a, b) => b.version - a.version)
        .map((v) => (
          <div key={v.confirmation_id} className="panel-inset px-4 py-3.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <Chip>version {v.version}</Chip>
              {v.responded_at ? (
                <Chip tone={v.response === 'confirmed' ? 'accent' : 'blue'}>
                  you {v.response === 'confirmed' ? 'confirmed this' : 'commented on this'}{' '}
                  {dateTime(v.responded_at)}
                </Chip>
              ) : (
                <Chip tone="amber">awaiting your response</Chip>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px] text-faint">
              <span>Sent to you {dateTime(v.sent_at)}</span>
              {v.companion_offered_at &&
                (v.companion_name ? (
                  <span>{v.companion_name} was with you</span>
                ) : (
                  <span>You were offered the right to be accompanied</span>
                ))}
            </div>

            <p className="measure mt-3 whitespace-pre-wrap text-[13px] leading-relaxed">
              {v.body}
            </p>

            {v.comment && (
              <div className="mt-3 rounded-md border-l-2 border-accent-line bg-accent-dim px-3 py-2">
                <div className="eyebrow mb-1">What you said in reply</div>
                <p className="measure whitespace-pre-wrap text-[12.5px] leading-relaxed text-ink">
                  {v.comment}
                </p>
              </div>
            )}

            {!v.responded_at && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  icon={CheckCircle2}
                  onClick={() => setResponding({ version: v.version, mode: 'confirmed' })}
                >
                  These are accurate
                </Button>
                <Button
                  size="sm"
                  icon={MessageSquareWarning}
                  onClick={() => setResponding({ version: v.version, mode: 'commented' })}
                >
                  Something is wrong
                </Button>
              </div>
            )}
          </div>
        ))}

      {responding && (
        <RespondDialog
          caseId={caseId}
          meetingId={meetingId}
          version={responding.version}
          mode={responding.mode}
          onClose={() => setResponding(null)}
          onDone={(next) => {
            setVersions(next)
            setResponding(null)
          }}
        />
      )}
    </div>
  )
}

function RespondDialog({ caseId, meetingId, version, mode, onClose, onDone }) {
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const confirming = mode === 'confirmed'

  return (
    <Modal
      open
      onClose={onClose}
      title={confirming ? 'Confirm these notes' : 'Say what is wrong'}
      subtitle={
        confirming
          ? 'You are saying this is an accurate record of what was said at your interview.'
          : 'Your words are added beside the notes. The notes themselves are not changed.'
      }
      width="max-w-xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="mb-4">
        <Notice tone="amber" title="You can only respond once to each version">
          {confirming
            ? 'Once you confirm, you cannot change this response. If the notes are later amended, you will be sent the new version and can respond to that.'
            : 'What you write stays on the record next to the notes. The notes are not altered — if the point is accepted, a new version is written and sent to you.'}
        </Notice>
      </div>

      <Field
        label={confirming ? 'Anything to add' : 'What is wrong with them'}
        required={!confirming}
        hint={
          confirming
            ? 'Optional. Anything you write here is kept beside the notes.'
            : 'Be specific. This sits next to the notes permanently.'
        }
      >
        <textarea
          className="field"
          rows={confirming ? 3 : 8}
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Field>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!confirming && !comment.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onDone(
                await write(`/${caseId}/my-notes/${meetingId}/respond`, {
                  body: {
                    version: String(version),
                    response: mode,
                    comment: comment.trim() || null,
                  },
                }),
              )
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          {confirming ? 'Confirm the notes' : 'Send my comment'}
        </Button>
      </div>
    </Modal>
  )
}
